import { ArticleV4DTO, BlockV4DTO, Asset_Image, Block_InlineAssetV4DTO } from 'publication-types'

import { imageWithCropsDtoToImageCrop } from '../../lib/to-image'
import {
    Block,
    EmbedBlock,
    TwinImageBlock,
    PullQuoteBlock,
    TextBlock,
    BlogBlock,
    SubheadBlock,
    ListBlock,
    CompetitionBlock,
    InlineImageBlock,
    InlineVideoBlock,
} from 'common/client-data-types/blocks'

export function getContentBlocks(publication: ArticleV4DTO): Block[] {
    const contentItem = publication.content

    if (contentItem && contentItem.kind === 'content') {
        return mapContentBlocks(contentItem.blocks)
    }

    return []
}

export function mapContentBlocks(blocks: BlockV4DTO[]) {
    const content: Block[] = []

    for (const apiBlock of blocks) {
        const block = mapContentBlock(apiBlock)

        if (block) {
            content.push(block)
        }
    }
    return content
}

export function mapContentBlock(block: BlockV4DTO): Block | undefined {
    switch (block.kind) {
        case 'embed': {
            const embedBlock: EmbedBlock = {
                kind: 'embed',
                link: block.reference,
            }
            return embedBlock
        }
        case 'adjacent-inlines': {
            const leftImageBlock = imageFromAsset(block.left, block.left.asset as Asset_Image)
            const rightImageBlock = imageFromAsset(block.right, block.right.asset as Asset_Image)

            const twinImage: TwinImageBlock = {
                kind: 'twin-image',
                left: leftImageBlock.inlineDetails,
                right: rightImageBlock.inlineDetails,
            }

            return twinImage
        }
        case 'inline': {
            const inlineBlock = inlineFromInlineAsset(block)

            return inlineBlock
        }
        case 'pull-quote': {
            const pullQuoteblock: PullQuoteBlock = {
                kind: block.kind,
                text: block.text,
                attribution: block.attribution || undefined,
            }
            return pullQuoteblock
        }
        case 'text': {
            const textBlock: TextBlock = {
                kind: block.kind,
                text: block.text,
                intentions: block.intentions,
            }
            return textBlock
        }
        case 'blog': {
            const blogBlock: BlogBlock = {
                kind: block.kind,
                blogId: block.blogId,
                provider: block.provider,
                blogState: block.blogState,
            }
            return blogBlock
        }
        case 'subhead': {
            const subheadBlock: SubheadBlock = {
                kind: block.kind,
                text: block.text,
                intentions: [],
            }
            return subheadBlock
        }
        case 'ordered-list':
        case 'unordered-list': {
            const listBlock: ListBlock = {
                kind: block.kind,
                items: block.items,
            }
            return listBlock
        }
        case 'competition': {
            const compBlock: CompetitionBlock = {
                kind: block.kind,
                id: block.id,
            }
            return compBlock
        }
        default:
            return undefined
    }
}

function imageFromAsset(inlineAsset: Block_InlineAssetV4DTO, imageAsset: Asset_Image) {
    const imageWithCrops = imageWithCropsDtoToImageCrop(imageAsset)
    if (imageAsset.userSelectedCrop) {
        imageWithCrops.userSelectedCrop = imageAsset.userSelectedCrop
    }

    const imageBlock: InlineImageBlock = {
        kind: inlineAsset.kind,
        inlineKind: imageAsset.kind,
        align: inlineAsset.align,
        inlineDetails: imageWithCrops,
    }

    return imageBlock
}

function inlineFromInlineAsset(inlineAsset: Block_InlineAssetV4DTO) {
    const asset = inlineAsset.asset
    if (!inlineAsset) {
        return
    }

    if (asset.kind === 'image' && asset.original) {
        const imageBlock = imageFromAsset(inlineAsset, asset)

        return imageBlock
    }

    if (asset.kind === 'video') {
        const videoBlock: InlineVideoBlock = {
            kind: inlineAsset.kind,
            inlineKind: asset.kind,
            inlineDetails: {
                captionText: asset.videoDescription,
            },
            inlineVideo: {
                id: asset.referenceId,
                accountId: asset.accountId,
                heading: asset.videoDescription,
                videoType: asset.videoType,
                posterImage: asset.posterImage
                    ? {
                          altText: asset.videoDescription,
                          crops:
                              typeof asset.posterImage.ratio === 'string'
                                  ? {
                                        [asset.posterImage.ratio]: {
                                            width: asset.posterImage.width,
                                            height: asset.posterImage.height,
                                            reference: asset.posterImage.reference,
                                        },
                                    }
                                  : {
                                        original: {
                                            width: asset.posterImage.width,
                                            height: asset.posterImage.height,
                                            reference: asset.posterImage.reference,
                                            ratio: asset.posterImage.ratio,
                                        },
                                    },
                      }
                    : undefined,
            },
        }
        return videoBlock
    }

    return
}
